import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DocumentArrowDownIcon,
  ClockIcon,
  BanknotesIcon,
  UserGroupIcon,
  FolderIcon,
  CalendarIcon,
  ChartPieIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline';
import { useTimeEntryStore } from '../store/timeEntryStore';
import { useThemeStore } from '../store/themeStore';
import {
  exportAPI, expensesAPI, clientsAPI, projectsAPI, timeEntriesAPI, categoriesAPI,
  ExpenseSummary, Client as ClientType, Project as ProjectType, TimeEntry,
} from '../services/api';
import { Button } from '../components/ui/Button';
import { Modal } from '../components/ui/Modal';
import {
  PieChart, Pie, Cell, Tooltip as RechartsTooltip, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend,
  LineChart, Line,
} from 'recharts';

const FALLBACK_COLORS = ['#10B981','#3B82F6','#8B5CF6','#F59E0B','#EF4444','#EC4899','#14B8A6','#6366F1','#D97706','#6B7280'];

// Local-timezone date string (YYYY-MM-DD) — never use toISOString (UTC shifts dates near midnight)
const getLocalDateString = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

// Monday of the week containing d (weeks run Monday–Sunday)
const mondayOnOrBefore = (d: Date): Date => {
  const r = new Date(d);
  const dow = (r.getDay() + 6) % 7; // Mon=0 … Sun=6
  r.setDate(r.getDate() - dow);
  r.setHours(0, 0, 0, 0);
  return r;
};

interface CategoryAgg {
  name: string;
  hours: number;
  entries: TimeEntry[];
  color: string;
}

export const ReportsPage: React.FC = () => {
  const { t } = useTranslation();
  const theme = useThemeStore((s) => s.theme);
  const isDark = theme === 'dark';
  const { rangeSummary, fetchRangeSummary, isLoading, error, clearError } = useTimeEntryStore();

  // Default to current month
  const today = new Date();
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

  const [startDate, setStartDate] = useState(firstDayOfMonth.toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(today.toISOString().split('T')[0]);
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState('');
  const [expenseSummary, setExpenseSummary] = useState<ExpenseSummary | null>(null);
  const [activeReportTab, setActiveReportTab] = useState<'time' | 'expenses' | 'weekly'>('time');
  const [clients, setClients] = useState<ClientType[]>([]);
  const [projects, setProjects] = useState<ProjectType[]>([]);
  const [filterClientId, setFilterClientId] = useState<string>('');
  const [filterProjectId, setFilterProjectId] = useState<string>('');
  const [activePeriodPreset, setActivePeriodPreset] = useState<'current' | 'previous' | null>(null);

  // Chart data state
  const [chartEntries, setChartEntries] = useState<TimeEntry[]>([]);
  const [isLoadingChart, setIsLoadingChart] = useState(false);
  const [categoryColors, setCategoryColors] = useState<Record<string, string>>({});
  const [histogramCategory, setHistogramCategory] = useState<string>('');
  const [drilldownEntries, setDrilldownEntries] = useState<TimeEntry[]>([]);
  const [drilldownTitle, setDrilldownTitle] = useState('');
  const [isDrilldownOpen, setIsDrilldownOpen] = useState(false);
  const [chartView, setChartView] = useState<'category' | 'subproject' | 'combined'>('category');

  // Weekly Summary report state
  const [weeklyGroupBy, setWeeklyGroupBy] = useState<'category' | 'subproject' | 'both'>('category');
  const [weeklyEntries, setWeeklyEntries] = useState<TimeEntry[]>([]);
  const [isLoadingWeekly, setIsLoadingWeekly] = useState(false);
  const weeklyInitialized = React.useRef(false);

  // Switch tabs; default the date range to the current calendar year the first time
  // the Weekly Summary tab is opened.
  const handleTabChange = (tab: 'time' | 'expenses' | 'weekly') => {
    if (tab === 'weekly' && !weeklyInitialized.current) {
      setStartDate(getLocalDateString(new Date(today.getFullYear(), 0, 1)));
      setEndDate(getLocalDateString(new Date(today.getFullYear(), 11, 31)));
      setActivePeriodPreset(null);
      weeklyInitialized.current = true;
    }
    setActiveReportTab(tab);
  };

  // Fetch clients and projects for filter dropdowns; auto-select if only one active project
  useEffect(() => {
    clientsAPI.list().then(setClients).catch(() => {});
    projectsAPI.list().then((allProjects) => {
      setProjects(allProjects);
      const activeProjects = allProjects.filter(p => p.is_active !== false);
      if (activeProjects.length === 1) {
        const proj = activeProjects[0];
        setFilterProjectId(proj.id);
        if (proj.client_id) setFilterClientId(proj.client_id);
      }
    }).catch(() => {});
  }, []);

  // Filtered projects based on selected client
  const filteredProjects = filterClientId
    ? projects.filter(p => p.client_id === filterClientId)
    : projects;

  // Get the selected project (if any) to check for reporting period
  const selectedProject = filterProjectId
    ? projects.find(p => p.id === filterProjectId)
    : null;

  // Auto-set date range to current reporting period when project changes
  useEffect(() => {
    if (!selectedProject) return;
    const dates = computePeriodDates(selectedProject, 'current');
    if (dates) {
      setStartDate(dates.start);
      setEndDate(dates.end);
      setActivePeriodPreset('current');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterProjectId]);

  useEffect(() => {
    if (startDate && endDate && startDate <= endDate) {
      fetchRangeSummary(startDate, endDate);
      expensesAPI.getSummary(startDate, endDate)
        .then(setExpenseSummary)
        .catch(() => setExpenseSummary(null));
    }
  }, [startDate, endDate, fetchRangeSummary]);

  // Fetch raw time entries for charts
  useEffect(() => {
    if (!startDate || !endDate || startDate > endDate) return;
    setIsLoadingChart(true);
    timeEntriesAPI.list({
      start_date: startDate,
      end_date: endDate,
      project_id: filterProjectId || undefined,
      client_id: filterClientId || undefined,
    }).then(setChartEntries).catch(() => setChartEntries([])).finally(() => setIsLoadingChart(false));
  }, [startDate, endDate, filterProjectId, filterClientId]);

  // Fetch raw time entries for the Weekly Summary, expanded to full Mon–Sun weeks
  // so partial edge weeks are complete (pulls in entries just outside the range).
  useEffect(() => {
    if (activeReportTab !== 'weekly') return;
    if (!startDate || !endDate || startDate > endDate) return;
    const rangeStart = mondayOnOrBefore(new Date(startDate + 'T00:00:00'));
    const rangeEnd = mondayOnOrBefore(new Date(endDate + 'T00:00:00'));
    rangeEnd.setDate(rangeEnd.getDate() + 6);
    setIsLoadingWeekly(true);
    timeEntriesAPI.list({
      start_date: getLocalDateString(rangeStart),
      end_date: getLocalDateString(rangeEnd),
      project_id: filterProjectId || undefined,
      client_id: filterClientId || undefined,
    }).then(setWeeklyEntries).catch(() => setWeeklyEntries([])).finally(() => setIsLoadingWeekly(false));
  }, [activeReportTab, startDate, endDate, filterProjectId, filterClientId]);

  // Fetch category colors when project selected
  useEffect(() => {
    if (filterProjectId) {
      categoriesAPI.list(filterProjectId).then(cats => {
        const colors: Record<string, string> = {};
        cats.forEach(c => { colors[c.name] = c.color; });
        setCategoryColors(colors);
      }).catch(() => setCategoryColors({}));
    } else {
      setCategoryColors({});
    }
  }, [filterProjectId]);

  // Category aggregation for ring chart
  const categoryData: CategoryAgg[] = useMemo(() => {
    const map: Record<string, { name: string; hours: number; entries: TimeEntry[] }> = {};
    chartEntries.forEach(e => {
      const cat = (typeof e.category === 'string' ? e.category : e.category) || 'Uncategorized';
      if (!map[cat]) map[cat] = { name: cat, hours: 0, entries: [] };
      map[cat].hours += e.hours;
      map[cat].entries.push(e);
    });
    const sorted = Object.values(map).sort((a, b) => b.hours - a.hours);
    return sorted.map((item, i) => ({
      ...item,
      color: categoryColors[item.name] || FALLBACK_COLORS[i % FALLBACK_COLORS.length],
    }));
  }, [chartEntries, categoryColors]);

  // Subproject aggregation for ring chart
  const subprojectData: CategoryAgg[] = useMemo(() => {
    const map: Record<string, { name: string; hours: number; entries: TimeEntry[] }> = {};
    chartEntries.forEach(e => {
      const sub = e.subproject_name || 'None';
      if (!map[sub]) map[sub] = { name: sub, hours: 0, entries: [] };
      map[sub].hours += e.hours;
      map[sub].entries.push(e);
    });
    const sorted = Object.values(map).sort((a, b) => b.hours - a.hours);
    return sorted.map((item, i) => ({
      ...item,
      color: FALLBACK_COLORS[i % FALLBACK_COLORS.length],
    }));
  }, [chartEntries]);

  // Combined category + subproject aggregation for ring chart
  const combinedData: CategoryAgg[] = useMemo(() => {
    const map: Record<string, { name: string; hours: number; entries: TimeEntry[] }> = {};
    chartEntries.forEach(e => {
      const cat = (typeof e.category === 'string' ? e.category : e.category) || 'Uncategorized';
      const sub = e.subproject_name || 'None';
      const key = `${cat} / ${sub}`;
      if (!map[key]) map[key] = { name: key, hours: 0, entries: [] };
      map[key].hours += e.hours;
      map[key].entries.push(e);
    });
    const sorted = Object.values(map).sort((a, b) => b.hours - a.hours);
    return sorted.map((item, i) => ({
      ...item,
      color: FALLBACK_COLORS[i % FALLBACK_COLORS.length],
    }));
  }, [chartEntries]);

  // Active chart data based on view selection
  const activeChartData = chartView === 'category' ? categoryData : chartView === 'subproject' ? subprojectData : combinedData;

  // Weekly Summary table: rows = full weeks, columns = chosen dimension, cells = hours + % of week
  const weeklyTable = useMemo(() => {
    const colKey = (e: TimeEntry) => {
      const cat = (typeof e.category === 'string' ? e.category : e.category) || 'Uncategorized';
      const sub = e.subproject_name || 'None';
      if (weeklyGroupBy === 'subproject') return sub;
      if (weeklyGroupBy === 'both') return `${cat} / ${sub}`;
      return cat;
    };

    if (!startDate || !endDate || startDate > endDate) {
      return { columns: [] as string[], rows: [] as any[], colTotals: {} as Record<string, number>, grand: 0 };
    }

    // Build full Mon–Sun weeks covering the range
    const firstWeek = mondayOnOrBefore(new Date(startDate + 'T00:00:00'));
    const lastWeek = mondayOnOrBefore(new Date(endDate + 'T00:00:00'));
    const weeks: { start: Date; end: Date }[] = [];
    const cursor = new Date(firstWeek);
    while (cursor <= lastWeek) {
      const end = new Date(cursor);
      end.setDate(cursor.getDate() + 6);
      weeks.push({ start: new Date(cursor), end });
      cursor.setDate(cursor.getDate() + 7);
    }

    const colTotals: Record<string, number> = {};
    weeklyEntries.forEach(e => {
      const k = colKey(e);
      colTotals[k] = (colTotals[k] || 0) + e.hours;
    });
    const columns = Object.keys(colTotals).sort((a, b) => colTotals[b] - colTotals[a]);

    const rows = weeks.map(wk => {
      const startStr = getLocalDateString(wk.start);
      const endStr = getLocalDateString(wk.end);
      const cells: Record<string, number> = {};
      columns.forEach(c => { cells[c] = 0; });
      let total = 0;
      weeklyEntries.forEach(e => {
        if (e.date >= startStr && e.date <= endStr) {
          cells[colKey(e)] += e.hours;
          total += e.hours;
        }
      });
      return { start: wk.start, end: wk.end, cells, total };
    }).filter(r => r.total > 0); // omit weeks with no logged hours

    const grand = Object.values(colTotals).reduce((s, v) => s + v, 0);
    return { columns, rows, colTotals, grand };
  }, [weeklyEntries, startDate, endDate, weeklyGroupBy]);

  const formatWeekLabel = (start: Date, end: Date) =>
    `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;

  // Shared color per column for the weekly table's summary charts (palette matches the exports)
  const weeklyColors = useMemo(() => {
    const m: Record<string, string> = {};
    weeklyTable.columns.forEach((c, i) => { m[c] = FALLBACK_COLORS[i % FALLBACK_COLORS.length]; });
    return m;
  }, [weeklyTable.columns]);

  // Donut data: overall hours per dimension across the whole range
  const weeklyRingData = useMemo(
    () => weeklyTable.columns.map(c => ({ name: c, value: weeklyTable.colTotals[c] || 0 })),
    [weeklyTable]
  );

  // Line data: one point per week, one series per dimension
  const weeklyLineData = useMemo(
    () => weeklyTable.rows.map(r => {
      const row: Record<string, any> = { label: formatWeekLabel(r.start, r.end) };
      weeklyTable.columns.forEach(c => { row[c] = parseFloat((r.cells[c] || 0).toFixed(2)); });
      return row;
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [weeklyTable]
  );

  // Tooltip for the weekly donut — percentages are relative to the range total
  const WeeklyPieTooltip = ({ active, payload }: any) => {
    if (!active || !payload?.length) return null;
    const d = payload[0];
    const total = weeklyTable.grand;
    return (
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-3">
        <p className="text-sm font-medium text-gray-900 dark:text-white">{d.name}</p>
        <p className="text-xs text-gray-600 dark:text-gray-400">{d.value.toFixed(2)}h ({total > 0 ? ((d.value / total) * 100).toFixed(1) : 0}%)</p>
      </div>
    );
  };

  const handleWeeklyExport = async (format: 'pdf' | 'docx') => {
    setIsExporting(true);
    setExportError('');
    try {
      const params = {
        start_date: startDate,
        end_date: endDate,
        group_by: weeklyGroupBy,
        client_id: filterClientId || undefined,
        project_id: filterProjectId || undefined,
      };
      const blob = format === 'pdf'
        ? await exportAPI.weeklySummaryPdf(params)
        : await exportAPI.weeklySummaryDocx(params);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `weekly_summary_${startDate}_${endDate}.${format}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err: any) {
      setExportError(err.response?.data?.detail || `Failed to export ${format.toUpperCase()}`);
    } finally {
      setIsExporting(false);
    }
  };

  const totalChartHours = useMemo(() => chartEntries.reduce((s, e) => s + e.hours, 0), [chartEntries]);

  // Histogram aggregation
  const histogramData = useMemo(() => {
    if (chartEntries.length === 0) return [];
    const startMs = new Date(startDate + 'T00:00:00').getTime();
    const endMs = new Date(endDate + 'T00:00:00').getTime();
    const span = (endMs - startMs) / (1000 * 60 * 60 * 24);
    const bucketType: 'daily' | 'weekly' | 'monthly' = span <= 14 ? 'daily' : span <= 90 ? 'weekly' : 'monthly';

    // Generate bucket boundaries
    const buckets: { label: string; start: string; end: string; entries: TimeEntry[] }[] = [];
    const sd = new Date(startDate + 'T00:00:00');
    const ed = new Date(endDate + 'T00:00:00');

    if (bucketType === 'daily') {
      const d = new Date(sd);
      while (d <= ed) {
        const ds = d.toISOString().split('T')[0];
        buckets.push({
          label: d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }),
          start: ds, end: ds, entries: [],
        });
        d.setDate(d.getDate() + 1);
      }
    } else if (bucketType === 'weekly') {
      const d = new Date(sd);
      while (d <= ed) {
        const weekEnd = new Date(d);
        weekEnd.setDate(d.getDate() + 6);
        if (weekEnd > ed) weekEnd.setTime(ed.getTime());
        buckets.push({
          label: `${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${weekEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`,
          start: d.toISOString().split('T')[0],
          end: weekEnd.toISOString().split('T')[0],
          entries: [],
        });
        d.setDate(d.getDate() + 7);
      }
    } else {
      const d = new Date(sd.getFullYear(), sd.getMonth(), 1);
      while (d <= ed) {
        const monthEnd = new Date(d.getFullYear(), d.getMonth() + 1, 0);
        const bStart = d < sd ? sd.toISOString().split('T')[0] : d.toISOString().split('T')[0];
        const bEnd = monthEnd > ed ? ed.toISOString().split('T')[0] : monthEnd.toISOString().split('T')[0];
        buckets.push({
          label: d.toLocaleDateString('en-US', { month: 'long', year: d.getFullYear() !== sd.getFullYear() ? 'numeric' : undefined }),
          start: bStart, end: bEnd, entries: [],
        });
        d.setMonth(d.getMonth() + 1);
        d.setDate(1);
      }
    }

    // Filter entries by histogram category if set
    const entries = histogramCategory
      ? chartEntries.filter(e => (typeof e.category === 'string' ? e.category : e.category) === histogramCategory)
      : chartEntries;

    // Assign entries to buckets
    entries.forEach(e => {
      for (const b of buckets) {
        if (e.date >= b.start && e.date <= b.end) {
          b.entries.push(e);
          break;
        }
      }
    });

    // Build data for recharts
    if (histogramCategory) {
      return buckets.map(b => ({
        label: b.label,
        hours: parseFloat(b.entries.reduce((s, e) => s + e.hours, 0).toFixed(2)),
        _entries: b.entries,
      }));
    }

    // Stacked: one key per category
    const allCats = Array.from(new Set(entries.map(e => (typeof e.category === 'string' ? e.category : e.category) || 'Uncategorized')));
    return buckets.map(b => {
      const row: any = { label: b.label, _entries: b.entries };
      allCats.forEach(cat => {
        row[cat] = parseFloat(b.entries.filter(e => ((typeof e.category === 'string' ? e.category : e.category) || 'Uncategorized') === cat).reduce((s, e) => s + e.hours, 0).toFixed(2));
      });
      return row;
    });
  }, [chartEntries, startDate, endDate, histogramCategory]);

  // Unique category names for histogram stacking
  const histogramCategories = useMemo(() => {
    if (histogramCategory) return [histogramCategory];
    return Array.from(new Set(chartEntries.map(e => (typeof e.category === 'string' ? e.category : e.category) || 'Uncategorized')));
  }, [chartEntries, histogramCategory]);

  const getCatColor = (catName: string) => {
    const found = categoryData.find(c => c.name === catName);
    return found?.color || '#6B7280';
  };

  // Drill-down handlers
  const handlePieClick = (data: CategoryAgg) => {
    setDrilldownTitle(`${data.name} — ${data.hours.toFixed(2)}h`);
    setDrilldownEntries(data.entries);
    setIsDrilldownOpen(true);
  };

  const handleBarClick = (data: any, catName?: string) => {
    const entries: TimeEntry[] = data._entries || [];
    const filtered = catName ? entries.filter(e => ((typeof e.category === 'string' ? e.category : e.category) || 'Uncategorized') === catName) : entries;
    const title = catName ? `${data.label} — ${catName}` : data.label;
    setDrilldownTitle(title);
    setDrilldownEntries(filtered);
    setIsDrilldownOpen(true);
  };

  // Custom tooltip for charts
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-3">
        <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">{label || payload[0]?.name}</p>
        {payload.map((p: any, i: number) => (
          <p key={i} className="text-xs text-gray-600 dark:text-gray-400">
            <span className="inline-block w-2 h-2 rounded-full mr-1.5" style={{ backgroundColor: p.color || p.fill }} />
            {p.name !== 'hours' ? `${p.name}: ` : ''}{p.value.toFixed(2)}h
          </p>
        ))}
      </div>
    );
  };

  const PieTooltip = ({ active, payload }: any) => {
    if (!active || !payload?.length) return null;
    const d = payload[0];
    return (
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-3">
        <p className="text-sm font-medium text-gray-900 dark:text-white">{d.name}</p>
        <p className="text-xs text-gray-600 dark:text-gray-400">{d.value.toFixed(2)}h ({totalChartHours > 0 ? ((d.value / totalChartHours) * 100).toFixed(1) : 0}%)</p>
      </div>
    );
  };

  const handleExport = async (format: 'csv' | 'pdf', type: 'time' | 'expenses' = 'time') => {
    setIsExporting(true);
    setExportError('');

    try {
      let blob: Blob;
      let filename: string;

      const exportParams = {
        start_date: startDate,
        end_date: endDate,
        client_id: filterClientId || undefined,
        project_id: filterProjectId || undefined,
      };

      if (type === 'expenses') {
        blob = format === 'csv'
          ? await exportAPI.expensesCsv(exportParams)
          : await exportAPI.expensesPdf(exportParams);
        filename = `expense_report_${startDate}_${endDate}.${format}`;
      } else {
        blob = format === 'csv'
          ? await exportAPI.csv(exportParams)
          : await exportAPI.pdf(exportParams);
        filename = `time_report_${startDate}_${endDate}.${format}`;
      }

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err: any) {
      setExportError(err.response?.data?.detail || `Failed to export ${format.toUpperCase()}`);
    } finally {
      setIsExporting(false);
    }
  };

  const setPresetRange = (preset: 'today' | 'thisWeek' | 'thisMonth' | 'lastMonth' | 'thisYear') => {
    const now = new Date();
    let start: Date;
    let end: Date = now;

    switch (preset) {
      case 'today':
        start = now;
        break;
      case 'thisWeek':
        start = new Date(now);
        start.setDate(now.getDate() - now.getDay());
        break;
      case 'thisMonth':
        start = new Date(now.getFullYear(), now.getMonth(), 1);
        break;
      case 'lastMonth':
        start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        end = new Date(now.getFullYear(), now.getMonth(), 0);
        break;
      case 'thisYear':
        start = new Date(now.getFullYear(), 0, 1);
        break;
      default:
        return;
    }

    setStartDate(start.toISOString().split('T')[0]);
    setEndDate(end.toISOString().split('T')[0]);
    setActivePeriodPreset(null);
  };

  // Pure function: compute period date range from project config
  const computePeriodDates = (project: ProjectType, which: 'current' | 'previous'): { start: string; end: string } | null => {
    if (!project.reporting_period_type) return null;
    const now = new Date();
    const td = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const fmt = (d: Date) => d.toISOString().split('T')[0];
    const type = project.reporting_period_type;

    if (type === 'semi_monthly') {
      if (which === 'current') {
        if (td.getDate() <= 15) {
          return { start: fmt(new Date(td.getFullYear(), td.getMonth(), 1)), end: fmt(new Date(td.getFullYear(), td.getMonth(), 15)) };
        } else {
          const lastDay = new Date(td.getFullYear(), td.getMonth() + 1, 0).getDate();
          return { start: fmt(new Date(td.getFullYear(), td.getMonth(), 16)), end: fmt(new Date(td.getFullYear(), td.getMonth(), lastDay)) };
        }
      } else {
        if (td.getDate() <= 15) {
          const lastMonthEnd = new Date(td.getFullYear(), td.getMonth(), 0);
          return { start: fmt(new Date(lastMonthEnd.getFullYear(), lastMonthEnd.getMonth(), 16)), end: fmt(lastMonthEnd) };
        } else {
          return { start: fmt(new Date(td.getFullYear(), td.getMonth(), 1)), end: fmt(new Date(td.getFullYear(), td.getMonth(), 15)) };
        }
      }
    } else if (type === 'monthly') {
      if (which === 'current') {
        const lastDay = new Date(td.getFullYear(), td.getMonth() + 1, 0).getDate();
        return { start: fmt(new Date(td.getFullYear(), td.getMonth(), 1)), end: fmt(new Date(td.getFullYear(), td.getMonth(), lastDay)) };
      } else {
        return { start: fmt(new Date(td.getFullYear(), td.getMonth() - 1, 1)), end: fmt(new Date(td.getFullYear(), td.getMonth(), 0)) };
      }
    } else if (type === 'weekly') {
      const config = project.reporting_period_config || {};
      const dayMap: Record<string, number> = { Sunday: 0, Monday: 1, Tuesday: 2, Wednesday: 3, Thursday: 4, Friday: 5, Saturday: 6 };
      const startDow = dayMap[config.start_day || 'Monday'] ?? 1;
      const currentDow = td.getDay();
      const daysSinceStart = (currentDow - startDow + 7) % 7;
      const weekStart = new Date(td);
      weekStart.setDate(td.getDate() - daysSinceStart);
      if (which === 'previous') weekStart.setDate(weekStart.getDate() - 7);
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 6);
      return { start: fmt(weekStart), end: fmt(weekEnd) };
    } else if (type === 'bi_weekly') {
      const config = project.reporting_period_config || {};
      const anchorStr = config.anchor_date || fmt(td);
      const anchor = new Date(anchorStr + 'T00:00:00');
      const diffDays = Math.floor((td.getTime() - anchor.getTime()) / (1000 * 60 * 60 * 24));
      const periodNum = Math.floor(diffDays / 14);
      const periodStart = new Date(anchor);
      periodStart.setDate(anchor.getDate() + (which === 'previous' ? (periodNum - 1) * 14 : periodNum * 14));
      const periodEnd = new Date(periodStart);
      periodEnd.setDate(periodStart.getDate() + 13);
      return { start: fmt(periodStart), end: fmt(periodEnd) };
    }
    return null;
  };

  const setPeriodPreset = (which: 'current' | 'previous') => {
    if (!selectedProject) return;
    const dates = computePeriodDates(selectedProject, which);
    if (dates) {
      setStartDate(dates.start);
      setEndDate(dates.end);
      setActivePeriodPreset(which);
    }
  };

  const formatDateRange = () => {
    const start = new Date(startDate + 'T00:00:00');
    const end = new Date(endDate + 'T00:00:00');

    return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
  };

  const formatEntryDate = (dateString: string) => {
    const date = new Date(dateString + 'T00:00:00');
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  const axisColor = isDark ? '#9CA3AF' : '#6B7280';
  const gridColor = isDark ? '#374151' : '#E5E7EB';

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          {t('reports.title')}
        </h1>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
          View summaries and export your time tracking data
        </p>
      </div>

      {/* Date Range Selection */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-5">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          {t('reports.dateRange')}
        </h2>

        {/* Preset Buttons */}
        <div className="flex flex-wrap gap-2 mb-4">
          {[
            { key: 'today', label: 'Today' },
            { key: 'thisWeek', label: 'This Week' },
            { key: 'thisMonth', label: 'This Month' },
            { key: 'lastMonth', label: 'Last Month' },
            { key: 'thisYear', label: 'This Year' },
          ].map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setPresetRange(key as any)}
              className="px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
            >
              {label}
            </button>
          ))}
        </div>

        {/* Client / Project Filters */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Client
            </label>
            <select
              value={filterClientId}
              onChange={(e) => {
                setFilterClientId(e.target.value);
                setFilterProjectId('');
              }}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
            >
              <option value="">All Clients</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Project
            </label>
            <select
              value={filterProjectId}
              onChange={(e) => setFilterProjectId(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
            >
              <option value="">All Projects</option>
              {filteredProjects.map((p) => (
                <option key={p.id} value={p.id}>{p.name}{p.client_name ? ` (${p.client_name})` : ''}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Period-Aware Presets */}
        {selectedProject?.reporting_period_type && (
          <div className="flex flex-wrap gap-2 mb-4">
            <span className="text-xs text-gray-500 dark:text-gray-400 self-center mr-1">
              {selectedProject.reporting_period_type === 'semi_monthly' ? 'Semi-monthly' :
               selectedProject.reporting_period_type === 'monthly' ? 'Monthly' :
               selectedProject.reporting_period_type === 'weekly' ? 'Weekly' :
               selectedProject.reporting_period_type === 'bi_weekly' ? 'Bi-weekly' :
               selectedProject.reporting_period_type} periods:
            </span>
            <button
              onClick={() => setPeriodPreset('current')}
              className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                activePeriodPreset === 'current'
                  ? 'text-white bg-green-600 dark:bg-green-500 hover:bg-green-700 dark:hover:bg-green-600'
                  : 'text-green-700 dark:text-green-300 bg-green-100 dark:bg-green-900/30 hover:bg-green-200 dark:hover:bg-green-800/40'
              }`}
            >
              Current Period
            </button>
            <button
              onClick={() => setPeriodPreset('previous')}
              className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                activePeriodPreset === 'previous'
                  ? 'text-white bg-green-600 dark:bg-green-500 hover:bg-green-700 dark:hover:bg-green-600'
                  : 'text-green-700 dark:text-green-300 bg-green-100 dark:bg-green-900/30 hover:bg-green-200 dark:hover:bg-green-800/40'
              }`}
            >
              Previous Period
            </button>
          </div>
        )}

        {/* Date Inputs */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('reports.startDate')}
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => { setStartDate(e.target.value); setActivePeriodPreset(null); }}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('reports.endDate')}
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => { setEndDate(e.target.value); setActivePeriodPreset(null); }}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
            />
          </div>
        </div>

        {startDate > endDate && (
          <p className="mt-2 text-sm text-red-600 dark:text-red-400">
            Start date must be before or equal to end date
          </p>
        )}
      </div>

      {/* Report Type Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="flex space-x-8">
          <button
            onClick={() => handleTabChange('time')}
            className={`py-3 px-1 border-b-2 text-sm font-medium transition-colors ${
              activeReportTab === 'time'
                ? 'border-green-500 text-green-600 dark:text-green-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300'
            }`}
          >
            {t('reports.timeReports')}
          </button>
          <button
            onClick={() => handleTabChange('weekly')}
            className={`py-3 px-1 border-b-2 text-sm font-medium transition-colors ${
              activeReportTab === 'weekly'
                ? 'border-green-500 text-green-600 dark:text-green-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300'
            }`}
          >
            {t('reports.weeklySummary')}
          </button>
          <button
            onClick={() => handleTabChange('expenses')}
            className={`py-3 px-1 border-b-2 text-sm font-medium transition-colors ${
              activeReportTab === 'expenses'
                ? 'border-green-500 text-green-600 dark:text-green-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300'
            }`}
          >
            {t('reports.expenseReports')}
          </button>
        </nav>
      </div>

      {/* Error Messages */}
      {(error || exportError) && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-sm text-red-600 dark:text-red-400">{error || exportError}</p>
          <button
            onClick={() => {
              clearError();
              setExportError('');
            }}
            className="text-sm text-red-800 dark:text-red-300 underline mt-1"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Time Reports Tab */}
      {activeReportTab === 'time' && (<>
      {/* Summary Stats */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
        </div>
      ) : rangeSummary ? (
        <>
          {/* Overview Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-5">
              <div className="flex items-center">
                <div className="p-3 bg-green-100 dark:bg-green-900 rounded-lg">
                  <ClockIcon className="h-6 w-6 text-green-600 dark:text-green-400" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    {t('reports.totalHours')}
                  </p>
                  <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                    {Number(rangeSummary.total_hours).toFixed(2)}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-5">
              <div className="flex items-center">
                <div className="p-3 bg-blue-100 dark:bg-blue-900 rounded-lg">
                  <CalendarIcon className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    Total Entries
                  </p>
                  <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                    {rangeSummary.total_entries}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-5">
              <div className="flex items-center">
                <div className="p-3 bg-purple-100 dark:bg-purple-900 rounded-lg">
                  <UserGroupIcon className="h-6 w-6 text-purple-600 dark:text-purple-400" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    Active Clients
                  </p>
                  <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                    {rangeSummary.by_client.length}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* By Client Breakdown */}
          {rangeSummary.by_client.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {t('reports.byClient')}
                </h2>
              </div>
              <div className="divide-y divide-gray-200 dark:divide-gray-700">
                {rangeSummary.by_client.map((item) => (
                  <div key={item.client_id} className="px-6 py-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                          <UserGroupIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                        </div>
                        <div className="ml-3">
                          <p className="text-sm font-medium text-gray-900 dark:text-white">
                            {item.client_name}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {item.entry_count} entries
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-semibold text-gray-900 dark:text-white">
                          {item.total_hours.toFixed(2)} hrs
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {((item.total_hours / Number(rangeSummary.total_hours)) * 100).toFixed(1)}%
                        </p>
                      </div>
                    </div>
                    {/* Progress bar */}
                    <div className="mt-2 w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full"
                        style={{
                          width: `${(item.total_hours / Number(rangeSummary.total_hours)) * 100}%`,
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* By Project Breakdown */}
          {rangeSummary.by_project.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {t('reports.byProject')}
                </h2>
              </div>
              <div className="divide-y divide-gray-200 dark:divide-gray-700">
                {rangeSummary.by_project.map((item) => (
                  <div key={item.project_id} className="px-6 py-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="p-2 bg-purple-100 dark:bg-purple-900 rounded-lg">
                          <FolderIcon className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                        </div>
                        <div className="ml-3">
                          <p className="text-sm font-medium text-gray-900 dark:text-white">
                            {item.project_name}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {item.client_name} • {item.entry_count} entries
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-semibold text-gray-900 dark:text-white">
                          {item.total_hours.toFixed(2)} hrs
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {((item.total_hours / Number(rangeSummary.total_hours)) * 100).toFixed(1)}%
                        </p>
                      </div>
                    </div>
                    {/* Progress bar */}
                    <div className="mt-2 w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                      <div
                        className="bg-purple-600 h-2 rounded-full"
                        style={{
                          width: `${(item.total_hours / Number(rangeSummary.total_hours)) * 100}%`,
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ─── Charts: Ring + Histogram stacked full-width ─── */}
          {!isLoadingChart && chartEntries.length > 0 && (
            <div className="space-y-4">
              {/* Ring Chart: Hours by Category / Subproject / Combined */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
                <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <h2 className="text-base font-semibold text-gray-900 dark:text-white flex items-center">
                    <ChartPieIcon className="h-5 w-5 mr-2" />
                    {chartView === 'category' ? t('reports.hoursByCategory') : chartView === 'subproject' ? 'Hours by Subproject' : 'Hours by Category + Subproject'}
                  </h2>
                  <div className="flex rounded-lg border border-gray-300 dark:border-gray-600 overflow-hidden">
                    {([
                      { key: 'category', label: 'Category' },
                      { key: 'subproject', label: 'Subproject' },
                      { key: 'combined', label: 'Both' },
                    ] as const).map(({ key, label }) => (
                      <button
                        key={key}
                        onClick={() => setChartView(key)}
                        className={`px-3 py-1 text-xs font-medium transition-colors ${
                          chartView === key
                            ? 'bg-green-600 text-white'
                            : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600'
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="p-3">
                  {activeChartData.length > 0 ? (
                  <div className="flex flex-col md:flex-row items-center gap-4">
                    {/* Donut Chart with labels */}
                    <div className="w-full md:w-3/5 flex-shrink-0" style={{ height: 360 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={activeChartData}
                            dataKey="hours"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            innerRadius={60}
                            outerRadius={115}
                            paddingAngle={2}
                            cursor="pointer"
                            onClick={(_, index) => handlePieClick(activeChartData[index])}
                            label={({ cx, cy, midAngle, outerRadius: or, name, percent }: any) => {
                              const RADIAN = Math.PI / 180;
                              const radius = (or || 115) + 18;
                              const x = (cx || 0) + radius * Math.cos(-(midAngle || 0) * RADIAN);
                              const y = (cy || 0) + radius * Math.sin(-(midAngle || 0) * RADIAN);
                              if ((percent || 0) < 0.03) return null;
                              return (
                                <text x={x} y={y} fill={isDark ? '#D1D5DB' : '#374151'} textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central" fontSize={12}>
                                  {name} ({(percent * 100).toFixed(0)}%)
                                </text>
                              );
                            }}
                            labelLine={{ stroke: isDark ? '#6B7280' : '#9CA3AF', strokeWidth: 1 }}
                          >
                            {activeChartData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} stroke={isDark ? '#1F2937' : '#FFFFFF'} strokeWidth={2} />
                            ))}
                          </Pie>
                          <RechartsTooltip content={<PieTooltip />} />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    {/* Legend List */}
                    <div className="w-full md:w-2/5 space-y-1 max-h-80 overflow-y-auto">
                      {activeChartData.map((item) => (
                        <button
                          key={item.name}
                          onClick={() => handlePieClick(item)}
                          className="w-full flex items-center justify-between px-2 py-1.5 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors text-left"
                        >
                          <div className="flex items-center min-w-0">
                            <div className="w-2.5 h-2.5 rounded-full flex-shrink-0 mr-2" style={{ backgroundColor: item.color }} />
                            <span className="text-sm text-gray-900 dark:text-white break-words">{item.name}</span>
                          </div>
                          <div className="text-right flex-shrink-0 ml-2">
                            <span className="text-sm font-medium text-gray-900 dark:text-white">{item.hours.toFixed(2)}h</span>
                            <span className="text-xs text-gray-500 dark:text-gray-400 ml-1">
                              ({totalChartHours > 0 ? ((item.hours / totalChartHours) * 100).toFixed(1) : 0}%)
                            </span>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                  ) : (
                    <p className="text-sm text-gray-500 dark:text-gray-400 py-8 text-center">
                      No {chartView === 'category' ? 'category' : chartView === 'subproject' ? 'subproject' : ''} data for this selection
                    </p>
                  )}
                </div>
              </div>

              {/* Histogram: Hours Over Time */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
                <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <h2 className="text-base font-semibold text-gray-900 dark:text-white flex items-center">
                    <ChartBarIcon className="h-5 w-5 mr-2" />
                    {t('reports.hoursOverTime')}
                  </h2>
                  <select
                    value={histogramCategory}
                    onChange={(e) => setHistogramCategory(e.target.value)}
                    className="px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  >
                    <option value="">{t('reports.allCategories')}</option>
                    {categoryData.map(cat => (
                      <option key={cat.name} value={cat.name}>{cat.name}</option>
                    ))}
                  </select>
                </div>
                <div className="p-3" style={{ height: 280 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={histogramData}>
                      <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                      <XAxis
                        dataKey="label"
                        tick={{ fontSize: 10, fill: axisColor }}
                        tickLine={{ stroke: axisColor }}
                        axisLine={{ stroke: axisColor }}
                        interval="preserveStartEnd"
                        angle={histogramData.length > 7 ? -30 : 0}
                        textAnchor={histogramData.length > 7 ? 'end' : 'middle'}
                        height={histogramData.length > 7 ? 50 : 25}
                      />
                      <YAxis
                        tick={{ fontSize: 10, fill: axisColor }}
                        tickLine={{ stroke: axisColor }}
                        axisLine={{ stroke: axisColor }}
                        width={35}
                        label={{ value: 'Hours', angle: -90, position: 'insideLeft', fill: axisColor, fontSize: 11 }}
                      />
                      <RechartsTooltip content={<CustomTooltip />} cursor={{ fill: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)' }} />
                      {histogramCategory ? (
                        <Bar
                          dataKey="hours"
                          fill={getCatColor(histogramCategory)}
                          radius={[4, 4, 0, 0]}
                          cursor="pointer"
                          onClick={(data: any) => handleBarClick(data, histogramCategory)}
                        />
                      ) : (
                        histogramCategories.map(cat => (
                          <Bar
                            key={cat}
                            dataKey={cat}
                            stackId="a"
                            fill={getCatColor(cat)}
                            cursor="pointer"
                            onClick={(data: any) => {
                              handleBarClick(data, cat);
                            }}
                          />
                        ))
                      )}
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          )}

          {/* No Data */}
          {rangeSummary.total_entries === 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-12 text-center">
              <CalendarIcon className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-600" />
              <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
                {t('reports.noData')}
              </h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                No time entries found for {formatDateRange()}
              </p>
            </div>
          )}
        </>
      ) : null}

      {/* Time Export Section */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-5">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Export Time Data
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          Download your time tracking data for the selected period: {formatDateRange()}
        </p>
        <div className="flex flex-wrap gap-3">
          <Button
            onClick={() => handleExport('csv', 'time')}
            disabled={isExporting || !rangeSummary || rangeSummary.total_entries === 0}
            variant="secondary"
          >
            <DocumentArrowDownIcon className="h-5 w-5 mr-2" />
            {t('reports.exportCSV')}
          </Button>
          <Button
            onClick={() => handleExport('pdf', 'time')}
            disabled={isExporting || !rangeSummary || rangeSummary.total_entries === 0}
            variant="secondary"
          >
            <DocumentArrowDownIcon className="h-5 w-5 mr-2" />
            {t('reports.exportPDF')}
          </Button>
        </div>
      </div>
      </>)}

      {/* Weekly Summary Tab */}
      {activeReportTab === 'weekly' && (<>
        {/* Controls */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-5 space-y-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              {t('reports.weeklySummaryTitle')}
            </h2>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
              {t('reports.weeklySummaryDescription')}
            </p>
          </div>

          {/* Group-by toggle */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-2">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('reports.groupBy')}:
            </span>
            <div className="flex rounded-lg border border-gray-300 dark:border-gray-600 overflow-hidden w-fit">
              {([
                { key: 'category', label: t('reports.byCategory') },
                { key: 'subproject', label: t('reports.bySubproject') },
                { key: 'both', label: t('reports.byBoth') },
              ] as const).map(({ key, label }) => (
                <button
                  key={key}
                  onClick={() => setWeeklyGroupBy(key)}
                  className={`px-4 py-1.5 text-sm font-medium transition-colors ${
                    weeklyGroupBy === key
                      ? 'bg-green-600 text-white'
                      : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Export buttons */}
          <div className="flex flex-wrap gap-3">
            <Button
              onClick={() => handleWeeklyExport('docx')}
              disabled={isExporting || weeklyTable.columns.length === 0}
              variant="secondary"
            >
              <DocumentArrowDownIcon className="h-5 w-5 mr-2" />
              {t('reports.exportWord')}
            </Button>
            <Button
              onClick={() => handleWeeklyExport('pdf')}
              disabled={isExporting || weeklyTable.columns.length === 0}
              variant="secondary"
            >
              <DocumentArrowDownIcon className="h-5 w-5 mr-2" />
              {t('reports.exportPDF')}
            </Button>
          </div>
        </div>

        {/* Table */}
        {isLoadingWeekly ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
          </div>
        ) : weeklyTable.columns.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-12 text-center">
            <CalendarIcon className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-600" />
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
              {t('reports.noWeeklyData')}
            </h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {formatDateRange()}
            </p>
          </div>
        ) : (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <th className="sticky left-0 z-10 bg-white dark:bg-gray-800 px-4 py-3 text-left font-semibold text-gray-900 dark:text-white whitespace-nowrap">
                    {t('reports.week')}
                  </th>
                  {weeklyTable.columns.map(col => (
                    <th key={col} className="px-4 py-3 text-center font-semibold text-gray-900 dark:text-white whitespace-nowrap">
                      {col}
                    </th>
                  ))}
                  <th className="px-4 py-3 text-center font-semibold text-gray-900 dark:text-white whitespace-nowrap">
                    {t('reports.total')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {weeklyTable.rows.map((row, i) => (
                  <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                    <td className="sticky left-0 z-10 bg-white dark:bg-gray-800 px-4 py-2.5 text-gray-900 dark:text-white whitespace-nowrap">
                      {formatWeekLabel(row.start, row.end)}
                    </td>
                    {weeklyTable.columns.map(col => {
                      const hrs = row.cells[col] || 0;
                      const pct = row.total > 0 ? (hrs / row.total) * 100 : 0;
                      return (
                        <td key={col} className="px-4 py-2.5 text-center whitespace-nowrap">
                          {hrs > 0 ? (
                            <>
                              <span className="text-gray-900 dark:text-white font-medium">{hrs.toFixed(2)}h</span>
                              <span className="block text-xs text-gray-500 dark:text-gray-400">{pct.toFixed(1)}%</span>
                            </>
                          ) : (
                            <span className="text-gray-300 dark:text-gray-600">–</span>
                          )}
                        </td>
                      );
                    })}
                    <td className="px-4 py-2.5 text-center font-semibold text-gray-900 dark:text-white whitespace-nowrap">
                      {row.total.toFixed(2)}h
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-gray-300 dark:border-gray-600 bg-green-50 dark:bg-green-900/20">
                  <td className="sticky left-0 z-10 bg-green-50 dark:bg-green-900/20 px-4 py-3 font-semibold text-gray-900 dark:text-white whitespace-nowrap">
                    {t('reports.total')}
                  </td>
                  {weeklyTable.columns.map(col => {
                    const hrs = weeklyTable.colTotals[col] || 0;
                    const pct = weeklyTable.grand > 0 ? (hrs / weeklyTable.grand) * 100 : 0;
                    return (
                      <td key={col} className="px-4 py-3 text-center font-semibold text-gray-900 dark:text-white whitespace-nowrap">
                        {hrs.toFixed(2)}h
                        <span className="block text-xs font-normal text-gray-500 dark:text-gray-400">{pct.toFixed(1)}%</span>
                      </td>
                    );
                  })}
                  <td className="px-4 py-3 text-center font-semibold text-gray-900 dark:text-white whitespace-nowrap">
                    {weeklyTable.grand.toFixed(2)}h
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}

        {/* Summary charts — stacked full-width */}
        {!isLoadingWeekly && weeklyTable.columns.length > 0 && (
          <div className="space-y-4">
            {/* Donut: overall breakdown */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
              <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-base font-semibold text-gray-900 dark:text-white flex items-center">
                  <ChartPieIcon className="h-5 w-5 mr-2" />
                  Overall by {weeklyGroupBy === 'category' ? t('reports.byCategory') : weeklyGroupBy === 'subproject' ? t('reports.bySubproject') : t('reports.byBoth')}
                </h2>
              </div>
              <div className="p-3 flex flex-col md:flex-row items-center gap-4">
                <div className="w-full md:w-3/5 flex-shrink-0" style={{ height: 360 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={weeklyRingData}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={115}
                        paddingAngle={2}
                      >
                        {weeklyRingData.map((entry) => (
                          <Cell key={entry.name} fill={weeklyColors[entry.name]} stroke={isDark ? '#1F2937' : '#FFFFFF'} strokeWidth={2} />
                        ))}
                      </Pie>
                      <RechartsTooltip content={<WeeklyPieTooltip />} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="w-full md:w-2/5 space-y-1 max-h-80 overflow-y-auto">
                  {weeklyRingData.map((item) => (
                    <div key={item.name} className="flex items-center justify-between px-2 py-1.5 rounded-lg">
                      <div className="flex items-center min-w-0">
                        <div className="w-2.5 h-2.5 rounded-full flex-shrink-0 mr-2" style={{ backgroundColor: weeklyColors[item.name] }} />
                        <span className="text-sm text-gray-900 dark:text-white break-words">{item.name}</span>
                      </div>
                      <div className="text-right flex-shrink-0 ml-2">
                        <span className="text-sm font-medium text-gray-900 dark:text-white">{item.value.toFixed(2)}h</span>
                        <span className="text-xs text-gray-500 dark:text-gray-400 ml-1">
                          ({weeklyTable.grand > 0 ? ((item.value / weeklyTable.grand) * 100).toFixed(1) : 0}%)
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Line: trend over time */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
              <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-base font-semibold text-gray-900 dark:text-white flex items-center">
                  <ChartBarIcon className="h-5 w-5 mr-2" />
                  Trend Over Time
                </h2>
              </div>
              <div className="p-3" style={{ height: 380 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={weeklyLineData} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                    <XAxis
                      dataKey="label"
                      tick={{ fontSize: 10, fill: axisColor }}
                      tickLine={{ stroke: axisColor }}
                      axisLine={{ stroke: axisColor }}
                      interval="preserveStartEnd"
                      angle={weeklyLineData.length > 7 ? -30 : 0}
                      textAnchor={weeklyLineData.length > 7 ? 'end' : 'middle'}
                      height={weeklyLineData.length > 7 ? 60 : 25}
                    />
                    <YAxis
                      tick={{ fontSize: 10, fill: axisColor }}
                      tickLine={{ stroke: axisColor }}
                      axisLine={{ stroke: axisColor }}
                      width={35}
                      label={{ value: 'Hours', angle: -90, position: 'insideLeft', fill: axisColor, fontSize: 11 }}
                    />
                    <RechartsTooltip content={<CustomTooltip />} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    {weeklyTable.columns.map((col) => (
                      <Line
                        key={col}
                        type="monotone"
                        dataKey={col}
                        stroke={weeklyColors[col]}
                        strokeWidth={2}
                        dot={{ r: 2 }}
                        activeDot={{ r: 4 }}
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        )}
      </>)}

      {/* Expense Reports Tab */}
      {activeReportTab === 'expenses' && (<>
        {/* Expense Summary Stats */}
        {expenseSummary ? (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-5">
                <div className="flex items-center">
                  <div className="p-3 bg-green-100 dark:bg-green-900 rounded-lg">
                    <BanknotesIcon className="h-6 w-6 text-green-600 dark:text-green-400" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Expenses</p>
                    <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                      ${expenseSummary.total_amount.toFixed(2)}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-5">
                <div className="flex items-center">
                  <div className="p-3 bg-blue-100 dark:bg-blue-900 rounded-lg">
                    <CalendarIcon className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Entries</p>
                    <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                      {expenseSummary.total_expenses}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-5">
                <div className="flex items-center">
                  <div className="p-3 bg-purple-100 dark:bg-purple-900 rounded-lg">
                    <FolderIcon className="h-6 w-6 text-purple-600 dark:text-purple-400" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Categories</p>
                    <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                      {expenseSummary.by_category.length}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* By Category Breakdown */}
            {expenseSummary.by_category.length > 0 && (
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">By Category</h2>
                </div>
                <div className="divide-y divide-gray-200 dark:divide-gray-700">
                  {expenseSummary.by_category.map((item) => (
                    <div key={item.category_name} className="px-6 py-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <div
                            className="w-3 h-3 rounded-full mr-3"
                            style={{ backgroundColor: item.category_color || '#6B7280' }}
                          />
                          <div>
                            <p className="text-sm font-medium text-gray-900 dark:text-white">{item.category_name}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">{item.entry_count} expenses</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-semibold text-gray-900 dark:text-white">
                            ${item.total_amount.toFixed(2)}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {expenseSummary.total_amount > 0
                              ? ((item.total_amount / expenseSummary.total_amount) * 100).toFixed(1)
                              : '0.0'}%
                          </p>
                        </div>
                      </div>
                      <div className="mt-2 w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                        <div
                          className="h-2 rounded-full"
                          style={{
                            backgroundColor: item.category_color || '#6B7280',
                            width: `${expenseSummary.total_amount > 0 ? (item.total_amount / expenseSummary.total_amount) * 100 : 0}%`,
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* By Project Breakdown */}
            {expenseSummary.by_project.length > 0 && (
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">By Project</h2>
                </div>
                <div className="divide-y divide-gray-200 dark:divide-gray-700">
                  {expenseSummary.by_project.map((item) => (
                    <div key={item.project_id} className="px-6 py-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <div className="p-2 bg-purple-100 dark:bg-purple-900 rounded-lg">
                            <FolderIcon className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                          </div>
                          <div className="ml-3">
                            <p className="text-sm font-medium text-gray-900 dark:text-white">{item.project_name}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {item.client_name} • {item.entry_count} expenses
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-semibold text-gray-900 dark:text-white">
                            ${item.total_amount.toFixed(2)}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {expenseSummary.total_expenses === 0 && (
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-12 text-center">
                <BanknotesIcon className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-600" />
                <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No expense data</h3>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  No expenses found for {formatDateRange()}
                </p>
              </div>
            )}
          </>
        ) : null}

        {/* Expense Export Section */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-5">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Export Expense Data
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Download your expense data for the selected period: {formatDateRange()}
          </p>
          <div className="flex flex-wrap gap-3">
            <Button
              onClick={() => handleExport('csv', 'expenses')}
              disabled={isExporting || !expenseSummary || expenseSummary.total_expenses === 0}
              variant="secondary"
            >
              <DocumentArrowDownIcon className="h-5 w-5 mr-2" />
              Export CSV
            </Button>
            <Button
              onClick={() => handleExport('pdf', 'expenses')}
              disabled={isExporting || !expenseSummary || expenseSummary.total_expenses === 0}
              variant="secondary"
            >
              <DocumentArrowDownIcon className="h-5 w-5 mr-2" />
              Export PDF
            </Button>
          </div>
        </div>
      </>)}

      {/* ─── Drill-Down Modal ─── */}
      <Modal isOpen={isDrilldownOpen} onClose={() => setIsDrilldownOpen(false)} title={drilldownTitle} size="lg">
        <div className="max-h-96 overflow-y-auto">
          {drilldownEntries.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400 py-4 text-center">No entries</p>
          ) : (
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {drilldownEntries.map((entry) => (
                <div key={entry.id} className="py-3 px-1">
                  <div className="flex justify-between items-start">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {entry.description}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        {formatEntryDate(entry.date)}
                        {entry.project_name && ` · ${entry.project_name}`}
                        {entry.subproject_name && ` · ${entry.subproject_name}`}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {typeof entry.category === 'string' ? entry.category : entry.category}
                      </p>
                    </div>
                    <span className="ml-3 flex-shrink-0 inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                      {entry.hours.toFixed(2)}h
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
          {drilldownEntries.length > 0 && (
            <div className="pt-3 mt-3 border-t border-gray-200 dark:border-gray-700 flex justify-end">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Total: <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                  {drilldownEntries.reduce((s, e) => s + e.hours, 0).toFixed(2)}h
                </span>
              </span>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};
